import { Moon, LunarPhase } from "lunarphase-js";
import { format, addDays } from "date-fns";
import { Spread } from "~/types/room/types";

// Map spread names to LunarPhase enum values
const SPREAD_NAME_TO_PHASE: Record<string, LunarPhase> = {
  "New Moon": LunarPhase.NEW,
  "Waxing Crescent": LunarPhase.WAXING_CRESCENT,
  "First Quarter Moon": LunarPhase.FIRST_QUARTER,
  "Waxing Gibbous": LunarPhase.WAXING_GIBBOUS,
  "Full Moon": LunarPhase.FULL,
  "Waning Gibbous": LunarPhase.WANING_GIBBOUS,
  "Last Quarter Moon": LunarPhase.LAST_QUARTER,
  "Waning Crescent": LunarPhase.WANING_CRESCENT,
};

// Moon phase emojis
const PHASE_EMOJI: Record<LunarPhase, string> = {
  [LunarPhase.NEW]: "🌑",
  [LunarPhase.WAXING_CRESCENT]: "🌒",
  [LunarPhase.FIRST_QUARTER]: "🌓",
  [LunarPhase.WAXING_GIBBOUS]: "🌔",
  [LunarPhase.FULL]: "🌕",
  [LunarPhase.WANING_GIBBOUS]: "🌖",
  [LunarPhase.LAST_QUARTER]: "🌗",
  [LunarPhase.WANING_CRESCENT]: "🌘",
};

// Lunar cycle order for sequential display
const LUNAR_PHASE_ORDER: LunarPhase[] = [
  LunarPhase.NEW,
  LunarPhase.WAXING_CRESCENT,
  LunarPhase.FIRST_QUARTER,
  LunarPhase.WAXING_GIBBOUS,
  LunarPhase.FULL,
  LunarPhase.WANING_GIBBOUS,
  LunarPhase.LAST_QUARTER,
  LunarPhase.WANING_CRESCENT,
];

export interface MoonPhaseInfo {
  phase: LunarPhase;
  startDate: Date;
  endDate: Date;
  dateLabel: string;
  emoji: string;
  isCurrent: boolean;
}

/**
 * Find the start date of a given moon phase by searching backwards/forwards from today
 */
function findPhaseStartDate(targetPhase: LunarPhase, referenceDate: Date): Date {
  // Search backwards to find when this phase started
  let searchDate = new Date(referenceDate);
  const currentPhase = Moon.lunarPhase(searchDate);
  
  if (currentPhase === targetPhase) {
    // We're in this phase, search backwards to find when it started
    while (Moon.lunarPhase(searchDate) === targetPhase) {
      searchDate = addDays(searchDate, -1);
    }
    // We've gone one day too far back, so add a day
    return addDays(searchDate, 1);
  } else {
    // Search forward to find when this phase starts
    let daysSearched = 0;
    while (Moon.lunarPhase(searchDate) !== targetPhase && daysSearched < 30) {
      searchDate = addDays(searchDate, 1);
      daysSearched++;
    }
    return searchDate;
  }
}

/**
 * Find the end date of a given moon phase
 */
function findPhaseEndDate(targetPhase: LunarPhase, startDate: Date): Date {
  let searchDate = new Date(startDate);
  while (Moon.lunarPhase(searchDate) === targetPhase) {
    searchDate = addDays(searchDate, 1);
  }
  // Go back one day to get the last day of the phase
  return addDays(searchDate, -1);
}

/**
 * Format a date range like "Dec 5-10" or "Dec 29-Jan 2"
 */
function formatDateRange(startDate: Date, endDate: Date): string {
  const startMonth = format(startDate, "MMM");
  const endMonth = format(endDate, "MMM");
  const startDay = format(startDate, "d");
  const endDay = format(endDate, "d");

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}-${endDay}`;
  } else {
    return `${startMonth} ${startDay}-${endMonth} ${endDay}`;
  }
}

/**
 * Get info for all moon phases, calculated once and memoized
 */
let cachedPhaseInfo: MoonPhaseInfo[] | null = null;
let cacheDate: string | null = null;

export function getAllMoonPhaseInfo(): MoonPhaseInfo[] {
  const today = new Date();
  const todayStr = format(today, "yyyy-MM-dd");

  // Return cached data if still valid (same day)
  if (cachedPhaseInfo && cacheDate === todayStr) {
    return cachedPhaseInfo;
  }

  const currentPhase = Moon.lunarPhase(today);
  const phaseInfoList: MoonPhaseInfo[] = [];

  for (const phase of LUNAR_PHASE_ORDER) {
    const startDate = findPhaseStartDate(phase, today);
    const endDate = findPhaseEndDate(phase, startDate);
    const dateLabel = formatDateRange(startDate, endDate);

    phaseInfoList.push({
      phase,
      startDate,
      endDate,
      dateLabel,
      emoji: PHASE_EMOJI[phase],
      isCurrent: phase === currentPhase,
    });
  }

  // Cache the result
  cachedPhaseInfo = phaseInfoList;
  cacheDate = todayStr;

  return phaseInfoList;
}

/**
 * Get the current moon phase
 */
export function getCurrentMoonPhase(): LunarPhase {
  return Moon.lunarPhase(new Date());
}

/**
 * Get the moon phase info for a spread name
 */
export function getMoonPhaseInfoForSpread(spreadName: string): MoonPhaseInfo | null {
  const phase = SPREAD_NAME_TO_PHASE[spreadName];
  if (!phase) return null;

  const allPhases = getAllMoonPhaseInfo();
  return allPhases.find((p) => p.phase === phase) ?? null;
}

/**
 * Get the date label for a spread name (e.g., "Full Moon" -> "Dec 4")
 */
export function getDateLabelForSpread(spreadName: string): string | null {
  return getMoonPhaseInfoForSpread(spreadName)?.dateLabel ?? null;
}

/**
 * Get the emoji for a spread name (e.g., "Full Moon" -> "🌕")
 */
export function getEmojiForSpread(spreadName: string): string | null {
  return getMoonPhaseInfoForSpread(spreadName)?.emoji ?? null;
}

/**
 * Check if a spread is the current moon phase
 */
export function isCurrentMoonPhase(spreadName: string): boolean {
  const phase = SPREAD_NAME_TO_PHASE[spreadName];
  if (!phase) return false;
  return phase === getCurrentMoonPhase();
}

/**
 * Reorder moon cycle spreads so current phase is first, then sequential
 */
export function reorderMoonPhaseSpreads(spreads: Spread[]): Spread[] {
  const currentPhase = getCurrentMoonPhase();

  // Find index of current phase in the lunar order
  const currentIndex = LUNAR_PHASE_ORDER.indexOf(currentPhase);
  if (currentIndex === -1) return spreads;

  // Create a map of spread name to spread for quick lookup
  const spreadByName = new Map<string, Spread>();
  spreads.forEach((s) => spreadByName.set(s.name, s));

  // Build reordered list starting from current phase
  const reordered: Spread[] = [];
  for (let i = 0; i < LUNAR_PHASE_ORDER.length; i++) {
    const phaseIndex = (currentIndex + i) % LUNAR_PHASE_ORDER.length;
    const phase = LUNAR_PHASE_ORDER[phaseIndex];

    // Find the spread that matches this phase
    const matchingSpread = spreads.find(
      (s) => SPREAD_NAME_TO_PHASE[s.name] === phase
    );
    if (matchingSpread) {
      reordered.push(matchingSpread);
    }
  }

  // Add any spreads that didn't match (shouldn't happen, but just in case)
  spreads.forEach((s) => {
    if (!reordered.includes(s)) {
      reordered.push(s);
    }
  });

  return reordered;
}

